/**
 * TeamJobStopDialog: the question before a staff stop of a team's active
 * run, and the stop itself. The API deletes the run's Job, its pod and the
 * pod's log with it (routers/internal.py `stop_team_job`), and refuses a
 * run that has ended or one a scheduled job runs for every team
 * (team_jobs.stop_refusal), which the row shows on its disabled button.
 *
 * The page renders this once and keeps it mounted, so the mutation's
 * answer lands even if the dialog has closed (ConfirmDialog).
 */

import { useState } from 'react'
import { toast } from 'sonner'

import { getApiErrorDetail } from '@/api/client'
import { useStopTeamJobApiInternalTeamsTeamIdJobsJobSlugRunsJobNameStopPost } from '@/api/generated/internal/internal'
import { ConfirmDialog } from '@/components/shared/ConfirmDialog'

import type { RunTarget } from './TeamJobLogSheet'

export const STOP_DESCRIPTION =
  'Stopping deletes the run and its pod, and its log goes with them. The next stage of the pipeline does not start.'

interface TeamJobStopDialogProps {
  teamId: string
  teamName: string
  /** Kept after the dialog closes, so the question does not blank out while it fades. */
  target: RunTarget | null
  open: boolean
  onOpenChange: (open: boolean) => void
  /** The page's list, read again once the stop has answered either way. */
  onSettled: () => void
}

export function TeamJobStopDialog({
  teamId,
  teamName,
  target,
  open,
  onOpenChange,
  onSettled,
}: TeamJobStopDialogProps): React.JSX.Element {
  const [error, setError] = useState<string | null>(null)
  const stop = useStopTeamJobApiInternalTeamsTeamIdJobsJobSlugRunsJobNameStopPost({
    mutation: {
      onSuccess: (response) => {
        if (response.status === 200 && target !== null) {
          toast.success(`${target.displayName} is stopping (${response.data.job_name}).`)
        }
        onOpenChange(false)
      },
      onError: (failure: unknown) => {
        setError(getApiErrorDetail(failure) ?? 'The run did not stop.')
      },
      onSettled,
    },
  })
  return (
    <ConfirmDialog
      open={open}
      onOpenChange={(next) => {
        if (next) setError(null)
        onOpenChange(next)
      }}
      title={target === null ? '' : `Stop ${target.displayName} for ${teamName}?`}
      description={STOP_DESCRIPTION}
      confirmLabel="Stop run"
      pendingLabel="Stopping..."
      isPending={stop.isPending}
      destructive
      error={error}
      onConfirm={() => {
        if (target === null) return
        setError(null)
        stop.mutate({ teamId, jobSlug: target.jobSlug, jobName: target.jobName })
      }}
    />
  )
}
